import { useEffect, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import type {
  Control,
  FieldValues,
  Path,
  UseFormTrigger,
} from 'react-hook-form';

import { getDialCodeFromCountryCode } from '@xing-com/crate-jobs-domain-apply-helpers';
import { isValidPhoneNumberForCountry } from '@xing-com/crate-jobs-helpers';

import { TextField } from './text-field';

const PHONE_NUMBER_MAX_LENGTH = 15;

type Props<T extends FieldValues> = {
  countryCode?: string;
  control: Control<T>;
  path: Path<T>;
  required?: boolean;
  trigger?: UseFormTrigger<T>;
};

export const PhoneField = <T extends FieldValues>({
  path,
  control,
  countryCode,
  required = false,
  trigger,
}: Props<T>): React.ReactElement => {
  const phoneNumber = useWatch({ control, name: path });
  const phoneNumberRef = useRef(phoneNumber);
  phoneNumberRef.current = phoneNumber;

  useEffect(() => {
    if (phoneNumberRef.current) {
      trigger?.(path);
    }
  }, [countryCode, path, trigger]);

  return (
    <TextField
      control={control}
      path={path}
      labelCopy="JOBS_INSTANT_APPLY_FORM_PHONE_NUMBER_LABEL"
      required={required}
      validate={(value) =>
        !value ||
        isValidPhoneNumberForCountry(
          value,
          countryCode,
          countryCode ? getDialCodeFromCountryCode(countryCode) : undefined
        )
      }
      validateOnChange={() => trigger?.(path)}
      maxLength={PHONE_NUMBER_MAX_LENGTH}
      errorMessage="JOBS_INSTANT_APPLY_FORM_PHONE_FIELD_INVALID_ERROR"
      requiredErrorMessage="JOBS_INSTANT_APPLY_FORM_PHONE_FIELD_EMPTY_ERROR"
      autoComplete="tel-local"
    />
  );
};
